import styles from '../BankPanels.module.css';
import { AceTable } from '../../../AceTable';
import { useApplicationStatusUtils } from '@axo/shared/data-access/hooks';
import { ITableConfig } from '../../../../models/tableConfig';
import { classNames } from '@axo/shared/util/dom';
import {
  loan_application,
  loan_quote,
  Product,
} from '@axo/shared/data-access/types';
import { Button, Colors, Icon, Tag } from '@blueprintjs/core';
import { Actions } from '../../Actions';
import { currencyFormatter } from '../../../../utils/formatCurrency';
import { roundNumber } from '../../../../utils/roundNumber';
import { formatDuration } from '../../../../utils/formatDuration';
import { LoanRequestStatus } from '../../LoanRequestStatus';
import { IncompleteApplication } from '../../IncompleteApplication/IncompleteApplication.tsx';
import { IQuoteDetail } from '../../hooks/useBankPanelData.tsx';
import { IBankPanel } from '../IBankPanel.ts';
import { useGetLenderFilterResult } from '../../../../hooks/utils/useLenderFilterResult.tsx';
import { formatDatetime } from '../../../../utils/formatDate';
import { useLenderIsActiveWithSARR } from '../useLenderIsActiveWithSARR.tsx';
import { useGetQuoteMyPageRanking } from '../useGetQuoteMyPageRanking.tsx';
import { useApplicationDataStore } from '../../../../store/applicationDataStore.ts';
import { Bank } from '@axo/ui-content/features/Partners';
import type { BankCode } from '@axo/ui-content/features/Partners/Bank';
import { LoanQuoteTextField } from '../../QuoteDataForm/Fields/LoanQuoteTextField.tsx';
import { useBankPanelStore } from '../../../../store/bankPanelStore.ts';
import { LoanQuoteDateField } from '../../QuoteDataForm/Fields/LoanQuoteDateField.tsx';
import { LoanQuoteStatus } from '../../LoanQuoteStatus/LoanQuoteStatus.tsx';

export function Offers({ application, data, isLoading }: IBankPanel) {
  const applicationID = application.ID;
  const { isInAllApplicationStatusGroups } = useApplicationStatusUtils();
  const currencyFormat = currencyFormatter(application.Currency);
  const applicationIsIncomplete = application.Status
    ? isInAllApplicationStatusGroups(
        application.Status,
        loan_application.StatusGroup.Incomplete,
      )
    : true;

  const { getLenderFilterResult } = useGetLenderFilterResult(
    applicationID,
    data.map((quote) => quote.LoanRequestID),
  );
  const { lenderIsActiveWithSARR } = useLenderIsActiveWithSARR(applicationID);
  const getQuoteMyPageRanking = useGetQuoteMyPageRanking(applicationID);
  const { selectRequest, selectedLenderID } =
    useApplicationDataStore(applicationID);

  const getMostRecentlyActivatedQuote = (quotes: loan_quote.BankQuote[]) => {
    let active: loan_quote.BankQuote | undefined;
    quotes.forEach((quote) => {
      if (
        quote.ActivatedAt &&
        (!active?.ActivatedAt || quote.ActivatedAt > active.ActivatedAt)
      ) {
        active = quote;
      }
    });
    return active;
  };
  const activeQuote = getMostRecentlyActivatedQuote(data ?? []);

  const { isQuoteBeingEdited } = useBankPanelStore(applicationID);

  function handleClickedRequestTag(lenderID: string, requestID: string) {
    selectRequest({ lenderID, requestID });
  }

  const bankPanelConfig: ITableConfig<IQuoteDetail> = {
    columns: {
      LenderID: {
        label: 'Bank',
        className: styles.lenderCell,
        render: (_, item, groupedStatuses) => (
          <div
            data-variant={
              item.Product !== Product.SecuredLoan
                ? item.Lender.BrokerConfiguration?.find(
                    (config) => config.Broker === application.Meta?.Broker,
                  )?.Tier
                : '0'
            }
            key={item.ID}
            className={classNames(
              groupedStatuses['LenderID'].isInGroup && styles.grouped,
              groupedStatuses['LenderID'].isFirstInGroup && styles.firstInGroup,
              groupedStatuses['LenderID'].isLastInGroup && styles.lastInGroup,
              styles.lender,
            )}
          >
            <Button
              minimal
              onClick={() =>
                handleClickedRequestTag(item.LenderID, item.LoanRequestID)
              }
              rightIcon={
                item.LenderID === activeQuote?.LenderID && (
                  <Icon icon="tick-circle" />
                )
              }
              className={styles.lenderButton}
            >
              <div className={styles.logoContainer}>
                <Bank
                  code={item.Lender.ShortName as BankCode}
                  name={item.Lender.Name}
                  country={application.MarketCountry}
                />
              </div>
            </Button>
          </div>
        ),
        width: 'medium',
        sticky: true,
      },
      LoanRequest: {
        label: 'Loan request status',
        render: (_, item, groupedStatuses) => (
          <LoanRequestStatus
            applicationID={applicationID}
            item={item}
            groupedStatuses={groupedStatuses}
            onClick={handleClickedRequestTag}
            lenderFilterResult={getLenderFilterResult(item.LoanRequestID)}
          />
        ),
        className: styles.loanRequests,
        shadow: 'right',
      },
      Status: {
        label: 'Loan quote status',
        render: (_, item, groupedStatuses) => (
          <LoanQuoteStatus
            applicationID={applicationID}
            item={item}
            groupedStatuses={groupedStatuses}
          />
        ),
        className: styles.status,
      },
      ID: {
        label: 'MyPage',
        render: (value) => {
          const rank = getQuoteMyPageRanking(
            value ? String(value) : undefined,
          )?.ordinal;

          if (!rank) {
            return <Icon icon="eye-off" color={Colors.GRAY3} />;
          }

          return rank;
        },
      },
      Amount: {
        label: 'Amount',
        render: (value, item) => {
          // MV-2981: top-up quotes lead with the new debt (TopupAmount) under
          // Amount and the new total under New total amount — bindings swap with the
          // values so edits hit the field being displayed.
          const isTopup = !!item.TopupAmount && item.TopupAmount > 0;
          if (isQuoteBeingEdited(item.ID)) {
            return (
              <LoanQuoteTextField
                applicationID={applicationID}
                loanQuoteID={item.ID}
                name={`${item.ID}.${isTopup ? 'TopupAmount' : 'Amount'}`}
              />
            );
          }
          const displayValue = isTopup ? item.TopupAmount : value;
          return displayValue ? currencyFormat(displayValue.toString()) : '-';
        },
      },
      LenderMaxPossibleAmount: {
        label: 'Max possible amount',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.LenderMaxPossibleAmount`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      TopupAmount: {
        label: 'New total amount',
        render: (value, item) => {
          // MV-2981: see Amount column — swapped for top-up quotes.
          const isTopup = !!item.TopupAmount && item.TopupAmount > 0;
          if (isQuoteBeingEdited(item.ID)) {
            return (
              <LoanQuoteTextField
                applicationID={applicationID}
                loanQuoteID={item.ID}
                name={`${item.ID}.${isTopup ? 'Amount' : 'TopupAmount'}`}
              />
            );
          }
          const displayValue = isTopup ? item.Amount : value;
          return displayValue ? currencyFormat(displayValue.toString()) : '-';
        },
      },
      Duration: {
        label: 'Duration',
        render: (_, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.Duration`}
            />
          ) : item.Duration ? (
            formatDuration(item)
          ) : (
            '-'
          ),
      },
      EffectiveRate: {
        label: 'Eff. rate',
        render: (_, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.EffectiveRate`}
            />
          ) : item.EffectiveRate ? (
            roundNumber(item.EffectiveRate, 2).toString() + '%'
          ) : (
            '-'
          ),
      },
      NominalRate: {
        label: 'Nom. rate',
        render: (_, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.NominalRate`}
            />
          ) : item.NominalRate ? (
            roundNumber(item.NominalRate, 2).toString() + '%'
          ) : (
            '-'
          ),
      },
      AnnualRate: {
        label: 'APR',
        render: (_, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.AnnualRate`}
            />
          ) : item.AnnualRate ? (
            roundNumber(item.AnnualRate, 2).toString() + '%'
          ) : (
            '-'
          ),
      },
      MonthlyPayment: {
        label: 'Monthly payment',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.MonthlyPayment`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      TotalPayment: {
        label: 'Total payment',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.TotalPayment`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      StartupFee: {
        label: 'Startup fee',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.StartupFee`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      AdministrationFee: {
        label: 'Administration fee',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.AdministrationFee`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      RefinanceAmount: {
        label: 'Refinance amount',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteTextField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.RefinanceAmount`}
            />
          ) : value ? (
            currencyFormat(value.toString())
          ) : (
            '-'
          ),
      },
      AcceptedAt: {
        label: 'Accept at',
        render: (value) => (value ? formatDatetime(`${value}`) : '-'),
      },
      AcceptedBy: {
        label: 'Accept by',
        render: (_, item) =>
          item.LenderID === activeQuote?.LenderID && item.AcceptedBy ? (
            <div style={{ display: 'flex' }}>
              <Tag minimal intent="success" icon="flag">
                {item.AcceptedBy}
              </Tag>
            </div>
          ) : (
            '-'
          ),
      },
      DisbursedAt: {
        label: 'Disbursed at',
        render: (value, item) =>
          isQuoteBeingEdited(item.ID) ? (
            <LoanQuoteDateField
              applicationID={applicationID}
              loanQuoteID={item.ID}
              name={`${item.ID}.DisbursedAt`}
            />
          ) : value ? (
            formatDatetime(`${value}`)
          ) : (
            '-'
          ),
      },
    },
    extra: [
      {
        label: 'Created by',
        key: 'createdBy',
        render: (quote) => {
          return quote.LoanRequest.CreatedBy;
        },
      },
      {
        label: '',
        key: 'actions',
        render: (quote, groupedStatuses) => (
          <Actions
            application={application}
            quote={quote}
            data={data || []}
            groupedStatuses={groupedStatuses}
          />
        ),
        width: 'small',
        noTruncate: true,
        sticky: true,
        shadow: 'left',
      },
    ],
  };

  return (
    <>
      {applicationIsIncomplete ? (
        <IncompleteApplication application={application} />
      ) : (
        <AceTable<IQuoteDetail>
          config={bankPanelConfig}
          loading={isLoading}
          data={data}
          maxRows={10}
          uniqueRowIDKey="ID"
          groupingKeys={['LenderID', 'LoanRequestID', 'LoanQuoteID']}
          rowClassName={(row) =>
            classNames(
              row.LenderID === selectedLenderID && styles.selectedRow,
              row.ID === activeQuote?.ID && styles.activeRow,
              lenderIsActiveWithSARR(row.LenderID) && styles.sarrActive,
            )
          }
        />
      )}
    </>
  );
}
